import { useState, useMemo, useCallback } from 'react';

import { Flex, Box } from '@chakra-ui/react';
import { Checkbox } from '@mesh.ai/rabbit';

import { ArrowRightCircle } from 'assets/assetsV1/iconComponents';

import { TextLabel } from 'uiToolkitV1/Typography';

/**
 * HIERARCHICAL TREE SELECTOR COMPONENT
 *
 * This component renders a tree structure with checkboxes for multi-selection.
 * Key features:
 * - Expandable/collapsible nodes
 * - Multi-selection with parent-child relationship handling
 * - Three checkbox states: selected, partial (some children selected), unselected
 * - Select all functionality
 *
 * Data Flow:
 * 1. Tree data comes in as nested objects
 * 2. Gets flattened into a Map for efficient lookups
 * 3. Selection state is managed externally via props
 * 4. UI reflects current state and calls back on changes
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Selection state constants for tree checkboxes
 */
export const SELECTION_STATES = {
  SELECTED: 'selected',
  PARTIAL: 'partial',
  UNSELECTED: 'unselected',
} as const;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Represents a single node in the tree structure.
 * Each node has an ID, display name, and optional children.
 */
export type TreeNode = {
  id: string; // Unique identifier for the node
  name: string; // Display text shown in the UI
  children?: TreeNode[]; // Optional array of child nodes
};

/**
 * Extended node type used internally after flattening.
 * Includes parent reference for efficient tree traversal.
 */
type FlatNode = TreeNode & {
  parentId: string | null; // Reference to parent node ID (null for root nodes)
};

/**
 * Possible states for checkboxes in the tree.
 * - SELECTED: Node and all children are selected
 * - PARTIAL: Some (but not all) children are selected
 * - UNSELECTED: Node and all children are unselected
 */
type SelectionState = (typeof SELECTION_STATES)[keyof typeof SELECTION_STATES];

/**
 * Props for the main HierarchicalTreeSelector component
 */
type HierarchicalTreeSelectorProps = {
  data: TreeNode[]; // Tree structure to render
  selectedNodes?: Set<string>; // Currently selected node IDs
  onSelectionChange: (selectedNodes: Set<string>) => void; // Callback when selection changes
};

/**
 * Props for the TreeNodeComponent (recursive tree renderer)
 */
type TreeNodeComponentProps = {
  node: TreeNode; // Current node to render
  depth: number; // Nesting level (for indentation)
  expandedNodes: Set<string>; // Set of currently expanded node IDs
  toggleExpand: (id: string) => void; // Function to expand/collapse nodes
  selectedNodes: Set<string>; // Set of currently selected node IDs
  handleSelect: (node: TreeNode) => void; // Function to handle node selection
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts SelectionState to the format expected by @mesh.ai/rabbit Checkbox
 */
const getCheckboxState = (state: SelectionState): boolean | 'indeterminate' => {
  switch (state) {
    case SELECTION_STATES.SELECTED:
      return true;
    case SELECTION_STATES.PARTIAL:
      return 'indeterminate';
    case SELECTION_STATES.UNSELECTED:
    default:
      return false;
  }
};

/**
 * Flattens a hierarchical tree structure into a Map for O(1) lookups.
 *
 * Why flatten?
 * - Makes it easy to find any node by ID without traversing the tree
 * - Enables efficient parent-child relationship queries
 * - Simplifies descendant collection
 *
 * @param nodes - Array of tree nodes to flatten
 * @param parent - Parent node ID (null for root level)
 * @param map - Accumulator Map (used for recursion)
 * @returns Map where key=nodeId, value=node with parent reference
 */
const flattenTree = (
  nodes: TreeNode[],
  parent: string | null = null,
  map: Map<string, FlatNode> = new Map()
): Map<string, FlatNode> => {
  nodes.forEach((node) => {
    // Add current node to map with parent reference
    map.set(node.id, { ...node, parentId: parent });

    // Recursively process children, setting current node as their parent
    if (node.children && node.children.length > 0) {
      flattenTree(node.children, node.id, map);
    }
  });
  return map;
};

/**
 * Recursively collects all descendant node IDs for a given node.
 *
 * Used for: When selecting/deselecting a parent, we need to also
 * select/deselect all its descendants to maintain consistency.
 *
 * @param id - ID of the node whose descendants to collect
 * @param nodeMap - Flattened tree map for lookups
 * @returns Array of all descendant node IDs
 */
const getDescendants = (id: string, nodeMap: Map<string, FlatNode>): string[] => {
  const descendants: string[] = [];
  const node = nodeMap.get(id);

  // Base case: node doesn't exist or has no children
  if (!node || !node.children) return descendants;

  // Collect immediate children and their descendants recursively
  node.children.forEach((child) => {
    descendants.push(child.id); // Add child
    descendants.push(...getDescendants(child.id, nodeMap)); // Add child's descendants
  });

  return descendants;
};

/**
 * Determines the selection state of a node based on its children's states.
 *
 * Logic:
 * - Leaf nodes: selected if in selectedNodes set, otherwise unselected
 * - Parent nodes:
 *   - SELECTED if ALL children are selected
 *   - PARTIAL if SOME children are selected or partial
 *   - UNSELECTED if NO children are selected
 *
 * @param node - Node to check
 * @param selectedNodes - Set of currently selected node IDs
 * @returns Selection state for the node
 */
const getSelectionState = (node: TreeNode, selectedNodes: Set<string>): SelectionState => {
  // Leaf node: check if it's in the selected set
  if (!node.children || node.children.length === 0) {
    return selectedNodes.has(node.id) ? SELECTION_STATES.SELECTED : SELECTION_STATES.UNSELECTED;
  }

  // Parent node: aggregate children's states
  let selected = 0; // Count of fully selected children
  let partial = 0; // Count of partially selected children

  node.children.forEach((child) => {
    const state = getSelectionState(child, selectedNodes);
    if (state === SELECTION_STATES.SELECTED) selected += 1;
    else if (state === SELECTION_STATES.PARTIAL) partial += 1;
  });

  // Determine state based on children's states
  if (selected === node.children.length) return SELECTION_STATES.SELECTED; // All children selected
  if (selected > 0 || partial > 0) return SELECTION_STATES.PARTIAL; // Some children selected
  return SELECTION_STATES.UNSELECTED; // No children selected
};

// ============================================================================
// UI COMPONENTS
// ============================================================================

/**
 * Recursive component that renders a single tree node and its children.
 *
 * Features:
 * - Indentation based on depth
 * - Expand/collapse button for nodes with children
 * - Checkbox for selection
 * - Recursive rendering of children when expanded
 */
const TreeNodeComponent = ({
  node,
  depth,
  expandedNodes,
  toggleExpand,
  selectedNodes,
  handleSelect,
}: TreeNodeComponentProps) => {
  // Calculate current node's state and UI properties
  const state = getSelectionState(node, selectedNodes);
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <Box>
      {/* Node row with expand button, checkbox, and label */}
      <Flex
        align="center"
        p="8px"
        bg={isExpanded ? '#E5F8FF' : undefined} // Light blue background when expanded
        pl={`${8 + depth * 24}px`} // Indent based on depth (24px per level)
        cursor="pointer"
        sx={{ '& > .rabbit-ds': { display: 'flex', alignItems: 'center' } }}
      >
        {/* Expand/collapse button (only for nodes with children) */}
        {hasChildren ? (
          <Box
            as="button"
            onClick={() => toggleExpand(node.id)}
            mr="8px"
            borderRadius="4px"
            bg="transparent"
            border="none"
          >
            {isExpanded ? (
              <ArrowRightCircle fill="transparent" transform="rotate(90deg)" boxSize="16px" color="#0d9488" />
            ) : (
              <ArrowRightCircle fill="transparent" boxSize="16px" color="#0d9488" />
            )}
          </Box>
        ) : (
          // Spacer for leaf nodes to maintain alignment
          <Box w="16px" mr="8px" />
        )}

        {/* Checkbox and label */}
        <Checkbox checked={getCheckboxState(state)} onCheckedChange={() => handleSelect(node)} />
        <TextLabel ml="8px" userSelect="none" onClick={() => handleSelect(node)}>
          {node.name}
        </TextLabel>
      </Flex>

      {/* Recursively render children if node is expanded */}
      {isExpanded &&
        hasChildren &&
        node.children!.map((child) => (
          <TreeNodeComponent
            key={child.id}
            node={child}
            depth={depth + 1} // Increase depth for indentation
            expandedNodes={expandedNodes}
            toggleExpand={toggleExpand}
            selectedNodes={selectedNodes}
            handleSelect={handleSelect}
          />
        ))}
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Main HierarchicalTreeSelector component.
 *
 * Manages:
 * - Expanded/collapsed state of nodes
 * - Selection logic (including parent-child relationships)
 * - Select all functionality
 * - Tree rendering
 */
const HierarchicalTreeSelector = ({
  data,
  selectedNodes = new Set(),
  onSelectionChange,
}: HierarchicalTreeSelectorProps) => {
  // ========================================================================
  // STATE MANAGEMENT
  // ========================================================================

  /**
   * Tracks which nodes are expanded (showing children).
   * Uses Set for O(1) lookup performance.
   */
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  /**
   * Flattened tree map for efficient node lookups.
   * Recalculated when tree data changes.
   */
  const nodeMap = useMemo(() => flattenTree(data), [data]);

  // ========================================================================
  // EVENT HANDLERS
  // ========================================================================

  /**
   * Toggles the expanded state of a node.
   * Uses functional update to avoid stale closure issues.
   */
  const toggleExpand = useCallback((id: string): void => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id); // Collapse if currently expanded
      } else {
        next.add(id); // Expand if currently collapsed
      }
      return next;
    });
  }, []);

  /**
   * Handles selection/deselection of a node and its descendants.
   *
   * Logic:
   * - If node is not fully selected, select it and all descendants
   * - If node is fully selected, deselect it and all descendants
   * - This maintains parent-child selection consistency
   */
  const handleSelect = useCallback(
    (node: TreeNode): void => {
      const state = getSelectionState(node, selectedNodes);
      const newSelected = new Set(selectedNodes);
      const descendants = getDescendants(node.id, nodeMap);

      if (state !== SELECTION_STATES.SELECTED) {
        // Select node and all descendants
        newSelected.add(node.id);
        descendants.forEach((id) => newSelected.add(id));
      } else {
        // Deselect node and all descendants
        newSelected.delete(node.id);
        descendants.forEach((id) => newSelected.delete(id));
      }

      onSelectionChange(newSelected);
    },
    [selectedNodes, nodeMap, onSelectionChange]
  );

  // ========================================================================
  // SELECT ALL FUNCTIONALITY
  // ========================================================================

  /**
   * All node IDs in the tree (for select all functionality).
   */
  const allNodeIds = useMemo(() => Array.from(nodeMap.keys()), [nodeMap]);

  /**
   * Current state of the "Select All" checkbox.
   */
  const selectAllState: SelectionState = allNodeIds.every((id) => selectedNodes.has(id))
    ? SELECTION_STATES.SELECTED
    : SELECTION_STATES.UNSELECTED;

  /**
   * Handles select all / deselect all action.
   */
  const handleSelectAll = (): void => {
    if (selectAllState === SELECTION_STATES.SELECTED) {
      onSelectionChange(new Set()); // Deselect all
    } else {
      onSelectionChange(new Set(allNodeIds)); // Select all
    }
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <Box w="100%" maxW="100%" overflowX="auto" m="0 auto" bg="white" borderRadius="8px">
      {/* Header with "Select All" checkbox */}
      <Flex
        align="center"
        p="8px"
        borderBottom="1px solid #e7e7e7"
        sx={{ '& > .rabbit-ds': { display: 'flex', alignItems: 'center' } }}
      >
        <Checkbox id="hts-select-all" checked={getCheckboxState(selectAllState)} onCheckedChange={handleSelectAll} />
        <TextLabel ml="8px" userSelect="none" onClick={handleSelectAll}>
          Select all
        </TextLabel>
      </Flex>

      {/* Tree rendering area with scroll for large trees */}
      <Box maxH="500px" overflowY="auto">
        {data.map((node) => (
          <TreeNodeComponent
            key={node.id}
            node={node}
            depth={0} // Root nodes start at depth 0
            expandedNodes={expandedNodes}
            toggleExpand={toggleExpand}
            selectedNodes={selectedNodes}
            handleSelect={handleSelect}
          />
        ))}
      </Box>
    </Box>
  );
};

export default HierarchicalTreeSelector;
